import { Box, Text, Wrap, WrapItem, Flex } from '@chakra-ui/react';
import Image from 'next/image';

import { useRouter } from 'next/router';
import Page from '@inspire/ui/chakra/layout/Page';
import {
  EtablissementTypes,
  EnseignementTypes,
  FormatTypes,
  DureePiste,
} from '@inspire/data/static/etablissement-types';

import PisteContent from '@inspire/ui/pistes/PisteContent';
import Layout from 'components/layout/Layout';
import {
  useBackButton,
  useMyProfile,
  usePisteBySlug,
  useUser,
} from 'lib/hooks';
import { pistePath, PISTES_PATH } from 'lib/paths';

import EtablissementIcon from '@inspire/ui/assets/undraw/type_etablissement.svg';
import EnseignementIcon from '@inspire/ui/assets/undraw/type_enseignement.svg';
import FormatIcon from '@inspire/ui/assets/undraw/type_format.svg';
import ApprentissageIcon from '@inspire/ui/assets/undraw/apprentissage.svg';
import DureeIcon from '@inspire/ui/assets/undraw/duree.svg';
import StageIcon from '@inspire/ui/assets/undraw/stage.svg';
import Span from '@inspire/ui/chakra/Span';

import Link from 'components/general/Link';
import gcl from 'lib/graphql-client';
import PisteHeader from 'components/piste/PisteHeader';
import { isGatl, isRc } from '@inspire/data/helpers/user';
import { useRedirectIfNotAllowed } from 'components/auth/Redirects';

export const PisteAttribute = ({ label, icon }) => (
  <WrapItem>
    <Box w={[90, 140]} textAlign="center">
      <Image src={icon} width={90} height={90} alt={label} />
      <Text fontWeight="bold" my={0} fontSize={['sm', 'md']}>
        {label}
      </Text>
    </Box>
  </WrapItem>
);

const PistePage = ({ piste: initialPiste }) => {
  const { query } = useRouter();
  const { isLyceen } = useUser();
  const slug = Array.isArray(query.pisteSlug)
    ? query.pisteSlug[0]
    : query.pisteSlug;
  const { piste, isPisteLoading, refetchPiste } = usePisteBySlug(slug, {
    enabled: !!slug,
    refetchOnMount: true,
    initialData: { piste: initialPiste },
  });
  const {
    myProfile,
    myProfile: { isPostBac },
  } = useMyProfile();
  useBackButton(PISTES_PATH, { label: 'Les Pistes' });

  const preventContactEclaireur = isLyceen && isPostBac;

  useRedirectIfNotAllowed('/404', isPisteLoading || piste !== undefined);

  return (
    <>
      <Page
        isLoading={isPisteLoading}
        withPanel
        title={piste?.name}
        headingIsTitle={false}
        panelProps={{ noPadding: true, compactOnMobile: false }}
      >
        <PisteHeader piste={piste} refetchPiste={refetchPiste} />
        {isLyceen && (
          <Flex borderBottom="1px solid" borderBottomColor="gray.200">
            <Box
              flex="1"
              borderRight="solid 1px"
              borderRightColor="gray.300"
              borderBottom="4px solid"
              borderBottomColor="yellow.400"
            >
              <Text
                casing="uppercase"
                fontSize="sm"
                textAlign="center"
                fontWeight="bold"
              >
                <Span display={['none', 'inline']}>Découvre </Span>la piste
              </Text>
            </Box>
            {!preventContactEclaireur && (
              <Link
                as={Box}
                href={`${pistePath(slug)}/eclaireurs`}
                flex="1"
                borderBottom="4px solid"
                borderBottomColor="gray.200"
                _hover={{ bgColor: 'yellow.100' }}
              >
                <Text casing="uppercase" fontSize="sm" textAlign="center">
                  <Span display={['none', 'inline']}>
                    Contacte un étudiant Éclaireur
                  </Span>
                  <Span display={['inline', 'none']}>Étudiants Éclaireurs</Span>
                </Text>
              </Link>
            )}
          </Flex>
        )}
        <Wrap px={[4, 12]} pt={[4, 12]} pb={0} spacing={4}>
          {piste?.typeEtablissement && (
            <PisteAttribute
              label={EtablissementTypes[piste.typeEtablissement]}
              icon={EtablissementIcon}
            />
          )}
          {piste?.duree && (
            <PisteAttribute label={DureePiste[piste.duree]} icon={DureeIcon} />
          )}
          {piste?.typeEnseignement && (
            <PisteAttribute
              label={EnseignementTypes[piste.typeEnseignement]}
              icon={EnseignementIcon}
            />
          )}
          {piste?.typeFormat && (
            <PisteAttribute
              label={FormatTypes[piste.typeFormat]}
              icon={FormatIcon}
            />
          )}
          {piste?.disponibleEnApprentissage && (
            <PisteAttribute
              label={'Disponible en apprentissage'}
              icon={ApprentissageIcon}
            />
          )}
          {piste?.dureeStageTo > 0 && (
            <PisteAttribute
              label={`${
                piste.dureeStageFrom ? `${piste.dureeStageFrom} à ` : ''
              }${piste.dureeStageTo} semaine${
                piste.dureeStageTo > 1 ? 's' : ''
              } de stage`}
              icon={StageIcon}
            />
          )}
        </Wrap>
        <Box px={[4, 12]} py={[4, 8]}>
          <PisteContent
            content={
              piste?.contentBacPro && (isGatl(myProfile) || isRc(myProfile))
                ? piste.contentBacPro
                : piste?.content || piste?.contentBacPro
            }
          />
        </Box>
      </Page>
    </>
  );
};

const pisteQuery = `
query PisteBySlug($slug: String!) {
  piste(slug: $slug) {
    _id
    name
    slug
    coverPicture
    bacs
    chapo
    content
    contentBacPro
    updatedAt
    typeEtablissement
    typeEnseignement
    typeFormat
    duree
    disponibleEnApprentissage
    pisteFeedback {
      hasFeedback
      knewThePiste
    }
  }
}
`;

const pisteSlugsQuery = `
query Pistes {
  pistesGT: pistes(classeHasModules: false) {
    slug
  }
  pistesBP: pistes(classeHasModules: true) {
    slug
  }
}
`;

export const getStaticProps = async ({ params }) => {
  const result = await gcl.request(pisteQuery, { slug: params?.pisteSlug });
  const piste = result.piste ?? null;
  const revalidate = parseInt(
    process.env.REVALIDATE_STATIC_PAGE_INTERVAL_SECONDS || '10',
    10
  );
  return { props: { piste }, revalidate };
};

export const getStaticPaths = async () => {
  if (process.env.NO_SSG) {
    return { paths: [], fallback: false };
  }
  const result = await gcl.request(pisteSlugsQuery);
  const pistes = result.pistesGT?.concat(result.pistesBP);
  return {
    paths: pistes?.map(({ slug }) => ({ params: { pisteSlug: slug } })),
    fallback: true,
  };
};

PistePage.getWrappers = (page) => (
  <Layout variant="adaptative" rowVariant="gray" containerWidth="lg">
    {page}
  </Layout>
);

export default PistePage;
